import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { integrationApiService, IntegrationConfig, StoreLayoutSettings } from '../../../services/integrationApi';
import type { SpecMetafieldRow } from '../../ShopifySpecMetafieldsSection';
import type { MetafieldMappingRow } from '../../ShopifyMetafieldMappingSection';
import { useAppTranslation, TranslateFn } from '../../../i18n/useAppTranslation';
import { categorySlugs, motzuKeySet, suggestSpecLabel, visibleStoreDefinitions } from '../mappingModel';
import type { MappingMenuItem } from '../MappingSectionLayout';
import type { MappingSection, MappingSectionLink } from '../mappingSections';
import { MatchesV2Slot, MatchesViewProps, SpecMetafieldsV2Slot, SpecViewProps } from './slots';
import type { TaxonomyDrafts } from './TaxonomyStep';
import TypeCategoryView from './TypeCategoryView';
import { useListingMatchesToConfirm } from './useListingMatchesToConfirm';

export type { MappingView as MappingV2View } from '../mappingSections';

export interface MappingV2PanelProps {
  integration: IntegrationConfig;
  /** The section the URL asks for (`?section=`); the menu inside shows it and reports a new one through `onSection`. */
  section?: MappingSectionLink | null;
  onSection?: (section: MappingSection) => void;
  /** The pending layout and its setter; the parent (IntegrationMappingPanel) owns dirty state, Save and Discard. */
  storeLayout: StoreLayoutSettings | undefined;
  onStoreLayoutChange: (layout: StoreLayoutSettings) => void;
  specMetafields: SpecMetafieldRow[];
  onSpecMetafieldsChange: (rows: SpecMetafieldRow[]) => void;
  /** MOT-144: the general mapping's unsaved rows, passed to the spec view's table; absent hides it. */
  metafieldMappings?: MetafieldMappingRow[];
  onMetafieldMappingsChange?: (rows: MetafieldMappingRow[]) => void;
  metafieldMappingPlanLocked?: boolean;
  taxonomyDrafts: TaxonomyDrafts;
  onTaxonomyDraftsChange: (drafts: TaxonomyDrafts) => void;
  /** Bumped by the parent's Discard so every panel remounts from the saved state. */
  epoch?: number;
  onOpenProductFields?: () => void;
  /** Slots for the Spec metafields and Listing matches panels. Default: the v2 views (`SpecMetafieldsV2View`, `ListingMatchesV2View`) through the slot adapters. */
  specView?: React.ComponentType<SpecViewProps>;
  matchesView?: React.ComponentType<MatchesViewProps>;
}

const M = 'integrations.shopifyMapping.menu';

/**
 * The menu lines of the two "Fields & listings" sections, the same in both layouts: fillable metafields
 * filled (plus suggestions waiting, in the accent tone) and listing matches to confirm (caution while any wait).
 */
export function fieldsAndListingsStatus(
  t: TranslateFn,
  counts: { specFilled: number; specTotal: number; specSuggested?: number; matches: number; matchesCapped: boolean },
): { specs: Pick<MappingMenuItem, 'status' | 'tone'>; matches: Pick<MappingMenuItem, 'status' | 'tone'> } {
  const suggested = counts.specSuggested ?? 0;
  const filled = counts.specTotal > 0
    ? t(`${M}.status.specFilled`, { filled: counts.specFilled, total: counts.specTotal })
    : t(`${M}.status.specNone`);
  return {
    specs: suggested > 0
      ? { status: `${filled} · ${t(`${M}.status.specSuggested`, { count: suggested })}`, tone: 'info' }
      : { status: filled },
    matches: counts.matches > 0
      ? { status: t(`${M}.status.matchesToConfirm`, { count: `${counts.matches}${counts.matchesCapped ? '+' : ''}` }), tone: 'warn' }
      : { status: t(`${M}.status.matchesNone`) },
  };
}

/** Motzu Classifications with no Shopify Product Type in the layout: they use the Unmatched Product Rules. */
export const unmatchedCategoryCount = (layout: StoreLayoutSettings | undefined): number => {
  const map = layout?.categoryMap || {};
  return categorySlugs(layout).filter((slug) => !map[slug]?.trim()).length;
};

/**
 * The Shopify mapping editor in its v2 panels (flag `shopify_mapping_v2`), laid out as v3: Type &
 * category sections plus Spec metafields and Listing matches, all behind one section menu. State, the
 * unsaved bar and Save live in the parent.
 */
const MappingV2Panel: React.FC<MappingV2PanelProps> = ({
  integration,
  section,
  onSection,
  storeLayout,
  onStoreLayoutChange,
  specMetafields,
  onSpecMetafieldsChange,
  metafieldMappings,
  onMetafieldMappingsChange,
  metafieldMappingPlanLocked,
  taxonomyDrafts,
  onTaxonomyDraftsChange,
  epoch = 0,
  onOpenProductFields,
  specView: SpecView = SpecMetafieldsV2Slot,
  matchesView: MatchesView = MatchesV2Slot,
}) => {
  const { t } = useAppTranslation();
  const optionsQuery = useQuery({
    queryKey: ['shopify-spec-metafield-options', integration.id],
    queryFn: () => integrationApiService.getSpecMetafieldOptions(integration.id),
    staleTime: 5 * 60 * 1000,
  });
  const fieldsQuery = useQuery({
    queryKey: ['motzu-metafield-fields'],
    queryFn: () => integrationApiService.getMotzuMetafieldFields(),
    staleTime: Infinity,
  });
  const { count: pendingMatches, capped: pendingMatchesCapped } = useListingMatchesToConfirm(integration);

  // Only the definitions a spec can fill count; references and other unwritable types are listed apart.
  // Suggested: an empty one with a catalog spec of the same name, as the Spec metafields banner counts them.
  const spec = useMemo(() => {
    const fillable = visibleStoreDefinitions(optionsQuery.data?.definitions || [], motzuKeySet(fieldsQuery.data || []))
      .filter((definition) => definition.writable);
    const catalog = optionsQuery.data?.specCatalog?.length
      ? optionsQuery.data.specCatalog
      : (optionsQuery.data?.specLabels || []).map((label) => ({ label, productCount: 0, sample: '', kind: 'text' as const }));
    let filled = 0;
    let suggested = 0;
    for (const definition of fillable) {
      const isFilled = specMetafields.some(
        (row) => row.namespace === definition.namespace && row.key === definition.key && row.specLabel.trim(),
      );
      if (isFilled) filled += 1;
      else if (suggestSpecLabel(definition, catalog)) suggested += 1;
    }
    return { filled, suggested, total: fillable.length };
  }, [optionsQuery.data, fieldsQuery.data, specMetafields]);

  const status = fieldsAndListingsStatus(t, {
    specFilled: spec.filled,
    specTotal: spec.total,
    specSuggested: spec.suggested,
    matches: pendingMatches,
    matchesCapped: pendingMatchesCapped,
  });

  return (
    <TypeCategoryView
      key={`type-${epoch}`}
      integrationId={integration.id}
      layout={storeLayout}
      onChange={onStoreLayoutChange}
      taxonomyDrafts={taxonomyDrafts}
      onTaxonomyDraftsChange={onTaxonomyDraftsChange}
      section={section}
      onSection={onSection}
      extraSections={[
        {
          item: { id: 'spec-metafields', label: t('integrations.shopifyDetail.tabs.metafields'), ...status.specs },
          content: (
            <SpecView
              key={`specs-${epoch}`}
              integration={integration}
              rows={specMetafields}
              onChange={onSpecMetafieldsChange}
              onOpenProductFields={onOpenProductFields}
              mappingRows={metafieldMappings}
              onMappingChange={onMetafieldMappingsChange}
              mappingPlanLocked={metafieldMappingPlanLocked}
            />
          ),
        },
        {
          item: { id: 'listing-matches', label: t('integrations.shopifyDetail.tabs.matching'), ...status.matches },
          content: <MatchesView integration={integration} />,
        },
      ]}
    />
  );
};

export default MappingV2Panel;
